import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/api/auth';
import { getApiErrorMessage } from '@/utils/apiError';

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') ?? '';
  const token = searchParams.get('token') ?? '';
  const isLinkValid = useMemo(() => Boolean(email && token), [email, token]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError(t('auth.reset.passwordTooShort'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t('auth.reset.passwordMismatch'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await authApi.adminResetPassword({
        email,
        token,
        newPassword,
      });
      setSuccess(response.message || t('auth.reset.success'));
      window.setTimeout(() => navigate('/admin-login'), 1500);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t('auth.reset.failed')));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isLinkValid) {
    return (
      <div className="space-y-6 text-center">
        <h2 className="text-2xl font-bold">{t('auth.reset.invalidLinkTitle')}</h2>
        <p className="text-muted-foreground">{t('auth.reset.invalidLinkDesc')}</p>
        <Link to="/forgot-password" className="text-sm text-primary hover:underline">
          {t('auth.reset.requestAnother')}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold">{t('auth.reset.title')}</h2>
        <p className="mt-2 text-muted-foreground">{t('auth.reset.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm text-center">
            {error}
          </div>
        )}

        {success && (
          <div className="p-3 rounded-lg bg-success/10 text-success text-sm text-center">
            {success}
          </div>
        )}

        <div className="space-y-2">
          <label htmlFor="newPassword" className="block text-sm font-medium">
            {t('auth.reset.newPassword')}
          </label>
          <input
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t('auth.reset.newPasswordPlaceholder')}
            dir="ltr"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="confirmPassword" className="block text-sm font-medium">
            {t('auth.reset.confirmPassword')}
          </label>
          <input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
            className="w-full px-4 py-3 rounded-lg border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder={t('auth.reset.confirmPasswordPlaceholder')}
            dir="ltr"
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          {isLoading ? t('auth.reset.resetting') : t('auth.reset.submit')}
        </button>

        <div className="text-center">
          <Link to="/admin-login" className="text-sm text-muted-foreground hover:text-primary">
            {t('auth.reset.backToAdminLogin')}
          </Link>
        </div>
      </form>
    </div>
  );
}
